import invariant from "invariant";
import React from "react";
import { clear, sendURLRequest } from "./actions";
import { useDispatch, useSelector } from "react-redux";

export { default as reducer } from "./reducer";
export { clear, sendURLRequest };

export function getURLRequestFromState(state, index) {
	return (state.httpRequest && state.httpRequest[index]) || null;
};

// Default implementation for the send function of useHTTPInterface
function defaultSend(fetch, url, options) {
	return fetch(url, options);
}

export function useHTTPInterface(send, options) {
	if (options === undefined && !(typeof send === 'function')) {
		options = send;
		send = defaultSend;
	}
	const { name } = options || {};

	invariant(typeof send == 'function', `${send} should be a function`);

	// Generate a HTTPInterface instance and keep it for the duration of the component
	const ref = React.useRef(null);
	if (ref.current === null) {
		ref.current = new HTTPInterface(name || '');
	}
	const iface = ref.current;
	const dispatch = useDispatch();

	return useHTTPInterfacePromise(
		// Get the data from the state
		useSelector(iface.getFromState),
		// The run request function
		React.useCallback((...args) => {
			// Dispatch to the HTTPInterface
			const fetch = (...fetchArgs) => dispatch(iface.send(...fetchArgs));
			// call the user function with the arguments and
			// and the fetch function
			send(fetch, ...args);
		}, [dispatch, send, iface]),
		React.useCallback(() => dispatch(iface.clear()), [dispatch, iface])
	);
};

export function useHTTPInterfacePromise(request, run, clear) {
	invariant(typeof run == 'function', `${run} should be a function`);
	invariant(typeof clear == 'function', `${clear} should be a function`);
	invariant(request === null || typeof request === 'object', `argument request should be an object or null but is ${request}`);

	// De encapsulate the result
	const { pending, success, response } = request || {pending: false, success: null};
	invariant(typeof pending === 'boolean', `${pending} is not a boolean`);
	invariant(success === null || typeof success === 'boolean', `${success} is not null or a boolean`);

	// Remove the httpRequest remains after unmount
	React.useEffect(() => clear, [clear]);

	// the callback to call after sending has ended
	const [ sending, setSending ] = React.useState(null);

	// pending => false, the send request has finished
	React.useEffect(() => {
		if (pending === false && sending) {
			if (success) {
				sending.resolve(response);
			} else {
				sending.reject();
			}
			setSending(null);
		}
	// eslint-disable-next-line
	}, [pending]);

	// Send the data and return a promise to keep submitting
	const send = React.useCallback((...args) => {
		run(...args);
		return new Promise((resolve, reject) => setSending({ resolve, reject }));
	}, [run, setSending]);

	return {
		send,
		success,
		pending,
	};
}

export class HTTPInterface {
	constructor(index) {
		if (typeof index === 'string') {
			index = Symbol(index);
		}
		this._index = index;
	}

	getFromState = (state) => getURLRequestFromState(state, this._index);

	clear = () => clear(this._index);

	send = (url, options) => sendURLRequest(this._index, url, options);
}
